/** SUNTRONIX ONE — ONE RELATIONSHIPS, ADDRESS BOOK, GIFTING & ASSOCIATION DIRECTORY.
 *  References Customer/Vendor masters (no duplicates). Personal scope grant-based. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { HeartHandshake, Search, ShieldAlert } from "lucide-react";
import {
  Overview, DirectorySection, PeopleSection, HouseholdsSection, OrgsSection,
  AddressBook, OccasionsSection, CampaignsSection, CatalogSection,
  LabelsSection, HistorySection,
} from "./OneRelationshipsSections";

const TYPE_SECTION = { PERSON: "PEOPLE", HOUSEHOLD: "HOUSEHOLDS", CUSTOMER: "DIRECTORY",
  VENDOR: "DIRECTORY", CLUB: "CLUBS & ASSOCIATIONS" };

export default function OneRelationships() {
  const [access, setAccess] = useState(null);
  const [denied, setDenied] = useState(false);
  const [section, setSection] = useState("OVERVIEW");
  const [refreshKey, setRefreshKey] = useState(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    api.get("/relationships/access")
      .then(({ data }) => data.allowed ? setAccess(data) : setDenied(true))
      .catch(() => setDenied(true));
  }, []);
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/relationships/directory", { params: { q } })
        .then(({ data }) => setResults(data.items || []));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && box.current?.contains(e.target)) return;
      setResults(null);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close); };
  }, []);

  if (denied) return (
    <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="rel-denied">
      <ShieldAlert className="h-10 w-10 mx-auto text-slate-300" />
      <div className="text-sm text-slate-500">ONE Relationships is for Suntronix internal users only.</div>
    </div>);
  if (!access) return null;

  const AREAS = ["OVERVIEW", "DIRECTORY", "PEOPLE", "HOUSEHOLDS", "CLUBS & ASSOCIATIONS",
    "ADDRESS BOOK", "OCCASIONS", "GIFT CAMPAIGNS", "GIFT CATALOG", "LABELS", "HISTORY"];
  const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
  const bump = () => setRefreshKey((k) => k + 1);
  const common = { access, refreshKey, bump, goto: setSection };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4 relative" data-testid="one-relationships-page">
      <header className="flex flex-wrap items-center gap-3 bg-rose-950 text-white rounded-xl px-4 py-3">
        <div className="h-10 w-10 rounded-lg bg-rose-400 text-rose-950 grid place-items-center">
          <HeartHandshake className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-lg font-heading font-semibold tracking-wide">ONE RELATIONSHIPS</div>
          <div className="text-[10.5px] text-rose-200/80">
            Address book, gifting &amp; association directory — one source, no duplicate masters
            {access.personal ? " · personal scope enabled" : ""}
          </div>
        </div>
        <div className="relative" ref={box}>
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-rose-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="rel-search"
              placeholder='Search people, households, customers, clubs…'
              className="text-[12px] bg-rose-900/70 border border-rose-800 rounded-md pl-8 pr-3 py-1.5 w-80 placeholder:text-rose-400/60" />
          {results && (
            <div className="absolute z-40 top-10 left-0 w-[430px] max-h-96 overflow-y-auto bg-white text-slate-800 rounded-lg shadow-xl border border-slate-200 p-2"
                data-testid="rel-search-results">
              {results.length === 0 && <div className="text-[11px] text-slate-400 p-3">No matches.</div>}
              {results.map((it, i) => (
                <button key={i} data-testid={`rel-sr-${i}`}
                    onClick={() => { setQ(""); setResults(null);
                      setSection(TYPE_SECTION[it.type] || "DIRECTORY"); }}
                    className="w-full text-left text-[11px] px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2">
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {it.type}{it.master_ref ? " (MASTER)" : ""}</span>
                  <span className="flex-1">{it.name}</span>
                  <span className="text-slate-400">{it.detail || ""}</span>
                </button>))}
            </div>)}
        </div>
      </header>

      <div className="flex gap-4 items-start">
        <nav className="w-52 shrink-0 space-y-0.5" data-testid="rel-nav">
          {AREAS.map((s) => (
            <button key={s} onClick={() => setSection(s)} data-testid={`rel-nav-${slug(s)}`}
                className={"w-full text-left text-[11px] px-2.5 py-2 rounded-md " +
                  (section === s ? "bg-rose-950 text-white font-semibold"
                                 : "text-slate-600 hover:bg-slate-100")}>
              {s}</button>))}
        </nav>
        <div className="flex-1 min-w-0">
          {section === "OVERVIEW" && <Overview {...common} />}
          {section === "DIRECTORY" && <DirectorySection {...common} />}
          {section === "PEOPLE" && <PeopleSection {...common} />}
          {section === "HOUSEHOLDS" && <HouseholdsSection {...common} />}
          {section === "CLUBS & ASSOCIATIONS" && <OrgsSection {...common} />}
          {section === "ADDRESS BOOK" && <AddressBook {...common} />}
          {section === "OCCASIONS" && <OccasionsSection {...common} />}
          {section === "GIFT CAMPAIGNS" && <CampaignsSection {...common} />}
          {section === "GIFT CATALOG" && <CatalogSection {...common} />}
          {section === "LABELS" && <LabelsSection {...common} />}
          {section === "HISTORY" && <HistorySection {...common} />}
        </div>
      </div>
    </div>
  );
}
